import { useEffect } from 'react'
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet'
import L from 'leaflet'
import { useFirestoreGeneral, useFirestoreGeneralOrderBy } from '../../../firebase/useFirestore'
import Location from '../../../hooks/Location'
import useEnsureAdressLocation from '../../../hooks/useEnsureAdressLocation'

// Default Leaflet marker icons reference paths that webpack can't resolve out
// of the box (CRA bundles them under hashed filenames). Wire them by hand from
// the leaflet package so markers render at all.
// AI-CONTEXT: well-known react-leaflet + webpack quirk
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
})

/**
 * Vertelt Leaflet opnieuw hoe groot hij is zodra zijn vak van maat verandert.
 *
 * WAARVOOR DIT WEL IS: maatveranderingen die NA het opbouwen komen — het menu
 * inklappen, het venster verkleinen, een kaart die breder wordt. Daar is
 * `invalidateSize` Leaflets eigen antwoord op, en een ResizeObserver dekt ze
 * allemaal met één mechanisme.
 *
 * WAARVOOR DIT NADRUKKELIJK NIET IS: de kaart die in een verborgen tabblad wordt
 * opgebouwd. Dat was de oorspronkelijke bugmelding, en in de browser nagemeten
 * haalt `invalidateSize()` die niet terug — een venster-resize evenmin. Leaflet
 * meet zich één keer op, onthoudt 0 bij 0, en de tegels voor die 'weergave'
 * bestaan in zijn boekhouding dan al. Gemeten bij BuurtKlusBedrijf: vak
 * 1141×240, één tegel in plaats van tien. Dáárvoor is de `active`-prop op
 * MapCard: niet verbergen maar niet renderen. Zie je deze observer voor die fix
 * aan, dan bouw je precies wat aantoonbaar niet werkt.
 * AI-CONTEXT: See docs/pitfalls.md#ui-component-mounted-hidden-keeps-a-zero-box
 *
 * Updateverzoek BuurtKlusBedrijf, 2026-09-21.
 *
 * @returns {null}
 */
const InvalidateOnResize = () => {
    const map = useMap()

    useEffect(() => {
        const vak = map.getContainer()
        // jsdom kent ResizeObserver niet; daar valt er ook niets te meten.
        if (!vak || typeof ResizeObserver === 'undefined') return undefined

        const observer = new ResizeObserver(() => map.invalidateSize())
        observer.observe(vak)
        return () => observer.disconnect()
    }, [map])

    return null
}

// Tiny child component that re-centers the map when the location changes
// (the Adresses doc snapshot may arrive after MapContainer mounts).
const RecenterOnLocation = ({ lat, lng }) => {
    const map = useMap()
    useEffect(() => {
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            map.setView([lat, lng], map.getZoom())
        }
    }, [lat, lng, map])
    return null
}

/**
 * De kaart met de locatie van dit huishouden.
 *
 * @param {object} props
 * @param {boolean} [props.active] - Of het tabblad 'Overzicht' open staat
 * @returns {JSX.Element}
 */
const MapCard = ({ active = true }) => {
    const adressId = Location()[3]

    // Same Firestore source as AdressDetails — Firestore listener cache means
    // this is effectively free, just gives us the doc on this side of the tree.
    const adressList = useFirestoreGeneral('Adresses', 'ID', adressId || '')
    const adress = Array.isArray(adressList) && adressList.length > 0 ? adressList[0] : null

    const personaData = useFirestoreGeneralOrderBy(
        'PersonaData',
        'PersonaID',
        adressId || '',
        'Position',
        'asc'
    )

    const { location, status } = useEnsureAdressLocation(adress, personaData)

    const lat = location?.Lat
    const lng = location?.Lng
    const hasPoint = Number.isFinite(lat) && Number.isFinite(lng)

    return (
        <div className="new-dashboard-card new-dashboard-card--wide">
            <div className="new-dashboard-card-content">
                <h3 className="new-dashboard-card-title">Locatie</h3>
                {/* De kaart wordt PAS opgebouwd als het tabblad open staat.
                    Leaflet meet zichzelf één keer bij het opbouwen; gebeurt dat
                    in een blok met `display: none`, dan onthoudt hij een maat van
                    0 bij 0. Hij haalt dan één tegel op, laat die op `opacity: 0`
                    staan en komt daar niet meer uit -- `invalidateSize()` en zelfs
                    een venster-resize herstellen dat niet, want de tegels voor die
                    'weergave' bestaan in zijn boekhouding al. Alleen een herlading
                    hielp, en dat is precies wat BuurtKlusBedrijf meldde.
                    Gemeten: vak 1141×240 met één onzichtbare tegel in plaats van tien.
                    AI-CONTEXT: See docs/pitfalls.md#ui-component-mounted-hidden-keeps-a-zero-box
                    AI-CONTEXT: See docs/features/huishouden-kaart-laadt-niet.md */}
                {hasPoint && active ? (
                    <>
                        <div className="adress-dashboard-map-container">
                            <MapContainer
                                center={[lat, lng]}
                                zoom={14}
                                maxZoom={14}
                                minZoom={8}
                                scrollWheelZoom={false}
                                attributionControl={false}
                                style={{ height: '100%', width: '100%', borderRadius: 6 }}
                            >
                                <TileLayer
                                    url="https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:3857/{z}/{x}/{y}.png"
                                    maxZoom={14}
                                />
                                <Marker position={[lat, lng]} />
                                <RecenterOnLocation lat={lat} lng={lng} />
                                <InvalidateOnResize />
                            </MapContainer>
                        </div>
                        <p className="adress-dashboard-map-credit">
                            <a href="https://www.pdok.nl" target="_blank" rel="noreferrer">Kadaster</a>
                        </p>
                    </>
                ) : hasPoint ? null : status === 'resolving' ? (
                    <p className="adress-dashboard-empty">Locatie ophalen…</p>
                ) : status === 'missing-input' ? (
                    <p className="adress-dashboard-empty">Geen locatie bekend (vul Postcode + Huisnummer in op het tabblad Gegevens).</p>
                ) : status === 'failed' ? (
                    <p className="adress-dashboard-empty">Locatie niet gevonden.</p>
                ) : (
                    <p className="adress-dashboard-empty">Locatie ophalen…</p>
                )}
            </div>
        </div>
    )
}

export default MapCard
